import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plug, Check, X, Search, Play, ExternalLink, Star,
  ShoppingBag, Zap, Shield, Clock, ArrowRight, Info,
  Loader2, RefreshCw, Link, Unlink, Key, Eye, EyeOff,
  Settings, AlertCircle, Globe, Copy, BookOpen
} from "lucide-react";
import { oauthService } from "../../services/oauthService";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// ACHADO DA REVISÃO: era um ID fixo/simulado - corrigido para o ID real
// de quem está logado (mesmo padrão do resto do frontend).
const USUARIO_ID = authService.getUser()?.id || "";
const USUARIO_TIPO = authService.getUser()?.role || "afiliado";

// URLs de ajuda para cada marketplace
const MARKETPLACE_HELP = {
  mercado_livre: { url: "https://developers.mercadolivre.com.br/pt_br/crie-uma-aplicacao-no-mercado-livre", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID (App ID)", client_secret: "Client Secret" } },
  shopee: { url: "https://open.shopee.com/", fields: ["partner_id", "partner_key", "shop_id"], labels: { partner_id: "Partner ID", partner_key: "Partner Key", shop_id: "Shop ID" } },
  amazon: { url: "https://sellercentral.amazon.com.br/apps/authorize/consent", fields: ["client_id", "client_secret", "seller_id"], labels: { client_id: "Client ID", client_secret: "Client Secret", seller_id: "Seller ID" } },
  magalu: { url: "https://api.magalu.com/", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID", client_secret: "Client Secret" } },
  shein: { url: "https://openapi.sheincorp.com/", fields: ["api_key", "api_secret", "seller_id"], labels: { api_key: "API Key", api_secret: "API Secret", seller_id: "Seller ID" } },
  casas_bahia: { url: "https://api.viavarejo.com.br/", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID", client_secret: "Client Secret" } },
  americanas: { url: "https://api.b2w.io/", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID", client_secret: "Client Secret" } },
  aliexpress: { url: "https://oauth.aliexpress.com/", fields: ["client_id", "client_secret"], labels: { client_id: "App Key", client_secret: "App Secret" } },
  carrefour: { url: "https://api.carrefour.com.br/", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID", client_secret: "Client Secret" } },
  havan: { url: "https://www.havan.com.br/", fields: ["api_key", "api_secret"], labels: { api_key: "API Key", api_secret: "API Secret" } },
  shopify: { url: "https://partners.shopify.com/", fields: ["client_id", "client_secret", "shop_url"], labels: { client_id: "API Key", client_secret: "API Secret", shop_url: "URL da Loja" } },
  netshoes: { url: "https://api.netshoes.com.br/", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID", client_secret: "Client Secret" } },
  dafiti: { url: "https://sellercenter-api.dafiti.com.br/", fields: ["api_key", "api_secret"], labels: { api_key: "API Key", api_secret: "User ID" } },
  submarino: { url: "https://api.b2w.io/", fields: ["client_id", "client_secret"], labels: { client_id: "Client ID", client_secret: "Client Secret" } },
};

const Integrations = ({ setCurrentSection, userRole = "afiliado" }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [marketplaces, setMarketplaces] = useState([]);
  const [connections, setConnections] = useState([]);
  const [platformConnections, setPlatformConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingId, setConnectingId] = useState(null);
  
  // Modal de Credenciais
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [autoMode, setAutoMode] = useState(false);

  const categories = [
    { id: 'all', name: 'Todos', count: 0 },
    { id: 'marketplaces', name: 'Marketplaces', count: 0 },
    { id: 'ecommerce', name: 'E-commerce', count: 0 },
    { id: 'delivery', name: 'Entrega', count: 0 },
  ];

  const categoryMap = {
    'mercado_livre': 'marketplaces', 'shopee': 'marketplaces', 'amazon': 'marketplaces',
    'magalu': 'marketplaces', 'shein': 'marketplaces', 'casas_bahia': 'marketplaces',
    'americanas': 'marketplaces', 'aliexpress': 'marketplaces', 'carrefour': 'marketplaces',
    'havan': 'marketplaces', 'netshoes': 'marketplaces', 'submarino': 'marketplaces',
    'shopify': 'ecommerce', 'dafiti': 'marketplaces',
  };

  const extraData = {
    'mercado_livre': { logo: 'https://http2.mlstatic.com/frontend-assets/ml-web-navigation/ui-navigation/5.21.22/mercadolibre/logo__large_plus.png', description: 'Lider na America Latina', popular: true },
    'amazon': { logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg', description: 'Maior marketplace do mundo', popular: true },
    'shopee': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Shopee_logo.svg/2560px-Shopee_logo.svg.png', description: 'Marketplace em crescimento', popular: true },
    'magalu': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/Magalu_-_novo_logo.png/800px-Magalu_-_novo_logo.png', description: 'Varejo brasileiro', popular: true },
    'havan': { logo: 'https://www.havan.com.br/media/logo/stores/1/logo-havan.svg', description: 'Varejo brasileiro lider', popular: true },
    'shein': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/00/SHEIN_logo.svg/1280px-SHEIN_logo.svg.png', description: 'Fast fashion global' },
    'casas_bahia': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9f/Casas_Bahia_logo.svg/1200px-Casas_Bahia_logo.svg.png', description: 'Via Varejo' },
    'americanas': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Americanas_logo_2022.svg/1280px-Americanas_logo_2022.svg.png', description: 'B2W Marketplace' },
    'aliexpress': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Aliexpress_logo.svg/1280px-Aliexpress_logo.svg.png', description: 'Marketplace global' },
    'carrefour': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5b/Carrefour_logo.svg/1200px-Carrefour_logo.svg.png', description: 'Varejo frances no Brasil' },
    'shopify': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Shopify_logo_2018.svg/2560px-Shopify_logo_2018.svg.png', description: 'Plataforma e-commerce' },
    'netshoes': { logo: 'https://logodownload.org/wp-content/uploads/2017/05/netshoes-logo.png', description: 'Artigos esportivos' },
    'dafiti': { logo: 'https://logodownload.org/wp-content/uploads/2018/09/dafiti-logo.png', description: 'Moda e lifestyle' },
    'submarino': { logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Submarino_logo.svg/1200px-Submarino_logo.svg.png', description: 'B2W Marketplace' },
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [mpData, connData, platformData] = await Promise.all([
        oauthService.getMarketplaces(true).catch((e) => { console.error('Falha ao buscar marketplaces:', e); return { marketplaces: [] }; }),
        oauthService.getUserConnections(USUARIO_ID).catch((e) => { console.error('Falha ao buscar conexoes do usuario:', e); return { connections: [] }; }),
        oauthService.getPlatformConnections().catch((e) => { console.error('Falha ao buscar conexoes da plataforma:', e); return { connections: [] }; })
      ]);
      const processedMp = (mpData.marketplaces || []).map(mp => ({
        ...mp,
        category: categoryMap[mp.id] || 'marketplaces',
        logo: extraData[mp.id]?.logo,
        description: extraData[mp.id]?.description || mp.name,
        popular: extraData[mp.id]?.popular || false,
      }));
      setMarketplaces(processedMp);
      setConnections(connData.connections || []);
      setPlatformConnections(platformData.connections || []);
    } catch (error) {
      console.error('Erro ao carregar:', error);
      toast.error('Erro ao carregar integracoes');
    } finally {
      setLoading(false);
    }
  };

  const getConnection = (marketplaceId) => connections.find(c => c.marketplace_id === marketplaceId);
  const isConnected = (marketplaceId) => { const c = getConnection(marketplaceId); return c && c.status === 'active'; };
  const isPlatformReady = (marketplaceId) => platformConnections.find(c => c.marketplace_id === marketplaceId && (c.status === 'active' || c.status === 'platform_ready'));

  // Abrir modal de credenciais ao clicar Conectar
  const handleConnect = (marketplace) => {
    if (marketplace.status === 'coming_soon') {
      toast.info(`${marketplace.name} estara disponivel em breve!`);
      return;
    }
    if (isConnected(marketplace.id)) {
      toast.info(`Voce ja esta conectado ao ${marketplace.name}`);
      return;
    }
    setSelectedMarketplace(marketplace);
    setCredentials({});
    setShowSecrets({});
    setAutoMode(false);
    setShowCredentialsModal(true);
  };

  // Salvar credenciais do afiliado
  const handleSaveCredentials = async () => {
    if (!selectedMarketplace) return;
    const help = MARKETPLACE_HELP[selectedMarketplace.id] || { fields: ['client_id', 'client_secret'] };
    const requiredField = help.fields[0];
    
    if (!credentials[requiredField]) {
      toast.error(`Informe o ${help.labels?.[requiredField] || requiredField}`);
      return;
    }

    try {
      setConnectingId(selectedMarketplace.id);
      
      const response = await fetch(`${BACKEND_URL}/api/oauth/connect-api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: USUARIO_ID,
          user_type: USUARIO_TIPO,
          marketplace_id: selectedMarketplace.id,
          credentials: { ...credentials, auto_mode: autoMode }
        })
      });

      if (response.ok) {
        toast.success(`Conectado ao ${selectedMarketplace.name}! Robo pronto para postar.`);
        setShowCredentialsModal(false);
        setSelectedMarketplace(null);
        setCredentials({});
        await loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao conectar');
      }
    } catch (error) {
      toast.error('Erro ao salvar credenciais');
    } finally {
      setConnectingId(null);
    }
  };

  const handleDisconnect = async (marketplace) => {
    if (!window.confirm(`Deseja desconectar do ${marketplace.name}?`)) return;
    try {
      setConnectingId(marketplace.id);
      await oauthService.disconnect(USUARIO_ID, marketplace.id);
      toast.success(`Desconectado do ${marketplace.name}`);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao desconectar');
    } finally { setConnectingId(null); }
  };

  const handleRefresh = async (marketplace) => {
    try {
      setConnectingId(marketplace.id);
      await oauthService.refreshToken(USUARIO_ID, marketplace.id);
      toast.success('Token atualizado!');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao atualizar token');
    } finally { setConnectingId(null); }
  };

  const filteredIntegrations = marketplaces.filter(int => {
    const matchCategory = selectedCategory === 'all' || int.category === selectedCategory;
    const matchSearch = int.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  const categoriesWithCount = categories.map(cat => ({
    ...cat,
    count: cat.id === 'all' ? marketplaces.length : marketplaces.filter(m => m.category === cat.id).length
  }));

  const connectedCount = connections.filter(c => c.status === 'active').length;
  const platformReadyCount = marketplaces.filter(m => isPlatformReady(m.id)).length;

  const steps = [
    { num: 1, title: 'Conecte', desc: 'Suas credenciais' },
    { num: 2, title: 'Selecione', desc: 'Produtos e fornecedor' },
    { num: 3, title: 'Credito', desc: 'Saldo na carteira' },
    { num: 4, title: 'Robo posta!', desc: 'Automaticamente' },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-950 overflow-auto h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full uppercase">Integracoes</span>
            <h1 data-testid="integrations-title" className="text-xl font-bold text-white">Minhas Integrações</h1>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} className="border-gray-700" data-testid="refresh-integrations-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
        <p className="text-sm text-gray-400">Conecte seus marketplaces para o robô publicar automaticamente</p>
      </div>

      {/* Fluxo para Afiliados */}
      {userRole !== 'admin' && (
        <Card data-testid="platform-banner" className="bg-gradient-to-r from-orange-500/15 to-orange-600/15 border-orange-500/40 p-4 mb-4">
          <h3 className="text-white text-base font-bold mb-2">Como comecar a vender:</h3>
          <div className="flex gap-2 flex-wrap">
            {steps.map((step) => (
              <div key={step.num} className="flex items-center gap-2 bg-gray-800/60 rounded-lg px-3 py-2 flex-1 min-w-[140px]">
                <span className="w-7 h-7 bg-orange-500 rounded-full text-sm text-white font-bold flex items-center justify-center flex-shrink-0">{step.num}</span>
                <div>
                  <p className="text-white text-xs font-semibold">{step.title}</p>
                  <p className="text-gray-400 text-[10px]">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="bg-green-500/10 border-green-500/30 p-3">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-gray-400 text-xs">Suas Conexoes</p>
              <p className="text-green-400 text-xl font-bold">{connectedCount}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-orange-500/10 border-orange-500/30 p-3">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-orange-400" />
            <div>
              <p className="text-gray-400 text-xs">Disponiveis</p>
              <p className="text-orange-400 text-xl font-bold">{marketplaces.filter(m => m.status !== 'coming_soon').length}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30 p-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-gray-400 text-xs">Plataforma</p>
              <p className="text-blue-400 text-xl font-bold">{platformReadyCount}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar marketplace..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-gray-800 border-gray-700 h-9 pl-9 text-sm" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categoriesWithCount.map((cat) => (
            <Button key={cat.id} size="sm" onClick={() => setSelectedCategory(cat.id)}
              className={`h-9 text-xs whitespace-nowrap ${selectedCategory === cat.id ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}>
              {cat.name} ({cat.count})
            </Button>
          ))}
        </div>
      </div>

      {/* Grid de Marketplaces */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filteredIntegrations.map((integration) => {
          const connected = isConnected(integration.id);
          const connection = getConnection(integration.id);
          const platformReady = isPlatformReady(integration.id);
          const isComingSoon = integration.status === 'coming_soon';
          const isLoading = connectingId === integration.id;

          return (
            <Card key={integration.id} data-testid={`marketplace-card-${integration.id}`}
              className={`bg-gray-900/50 border-gray-700 p-4 transition-all relative group ${connected ? 'border-green-500/50' : 'hover:border-orange-500/50'} ${isComingSoon ? 'opacity-60' : ''}`}>
              {integration.popular && (
                <span className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded z-10">Popular</span>
              )}
              {connected && (
                <span className="absolute -top-2 -left-2 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded z-10 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Conectado
                </span>
              )}
              
              <div className="w-full h-12 mb-3 flex items-center justify-center bg-white rounded-lg p-2">
                {integration.logo ? (
                  <img src={integration.logo} alt={integration.name} className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : null}
                <div className={`${integration.logo ? 'hidden' : 'flex'} w-10 h-10 rounded-full items-center justify-center text-white font-bold text-lg`}
                  style={{ backgroundColor: integration.color || '#666' }}>
                  {integration.name.charAt(0)}
                </div>
              </div>
              
              <h4 className="text-white text-sm font-semibold mb-1">{integration.name}</h4>
              <p className="text-gray-400 text-xs mb-1">{integration.description}</p>
              <p className="text-gray-500 text-[10px] mb-2">
                {integration.auth_type === 'oauth2' ? 'OAuth 2.0' : integration.auth_type === 'oauth2_signature' ? 'OAuth + Signature' : 'API Key'}
              </p>
              
              {connected && connection && (
                <div className="text-[10px] text-gray-500 mb-2 bg-gray-800/50 rounded p-1.5">
                  <p>Seller: {connection.seller_id || 'Conectado'}</p>
                </div>
              )}

              {!connected && platformReady && userRole !== 'admin' && (
                <div className="text-[10px] text-emerald-400 mb-2 bg-emerald-500/10 rounded p-1.5">
                  <p className="flex items-center gap-1"><Shield className="w-3 h-3" /> Plataforma pronta</p>
                  <p className="text-gray-400">Conecte sua conta para vender</p>
                </div>
              )}
              
              <div className="flex items-center gap-1">
                {isComingSoon ? (
                  <Button disabled className="flex-1 bg-gray-700 h-7 text-xs"><Clock className="w-3 h-3 mr-1" />Em breve</Button>
                ) : connected ? (
                  <>
                    <Button size="sm" variant="outline" className="flex-1 h-7 text-xs border-gray-600" onClick={() => handleRefresh(integration)} disabled={isLoading}>
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={() => handleDisconnect(integration)} disabled={isLoading}>
                      <Unlink className="w-3 h-3" />
                    </Button>
                  </>
                ) : (
                  <Button size="sm" className="flex-1 h-7 text-xs bg-orange-500 hover:bg-orange-600" onClick={() => handleConnect(integration)} disabled={isLoading}>
                    {isLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Key className="w-3 h-3 mr-1" />}
                    Conectar Conta
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Info */}
      <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
        <p className="text-blue-300 text-sm">
          Conecte seus marketplaces com suas credenciais. Apos conectar, o robo publica automaticamente seus produtos. Cada afiliado tem anuncios unicos.
        </p>
      </div>

      {/* ============================================ */}
      {/* MODAL DE CREDENCIAIS DO MARKETPLACE          */}
      {/* ============================================ */}
      {showCredentialsModal && selectedMarketplace && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-orange-500" />
                  Conectar {selectedMarketplace.name}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => { setShowCredentialsModal(false); setSelectedMarketplace(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Instrucoes */}
              <Card className="bg-orange-500/10 border-orange-500/30 p-3 mb-4">
                <h4 className="text-orange-400 font-semibold text-sm mb-1 flex items-center gap-1">
                  <BookOpen className="w-4 h-4" /> Como obter suas credenciais:
                </h4>
                <ol className="text-gray-300 text-xs space-y-1 ml-4 list-decimal">
                  <li>Acesse sua conta no {selectedMarketplace.name}</li>
                  <li>Va em <strong>Configuracoes</strong> ou <strong>Developers/API</strong></li>
                  <li>Crie uma aplicacao ou copie suas credenciais</li>
                  <li>Cole os dados nos campos abaixo</li>
                </ol>
                {MARKETPLACE_HELP[selectedMarketplace.id]?.url && (
                  <a href={MARKETPLACE_HELP[selectedMarketplace.id].url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-orange-400 text-xs mt-2 hover:underline">
                    <ExternalLink className="w-3 h-3" />
                    Abrir pagina de credenciais do {selectedMarketplace.name}
                  </a>
                )}
              </Card>

              {/* Campos de credenciais */}
              <div className="space-y-3">
                {(MARKETPLACE_HELP[selectedMarketplace.id]?.fields || ['client_id', 'client_secret']).map((field) => {
                  const label = MARKETPLACE_HELP[selectedMarketplace.id]?.labels?.[field] || field;
                  const isSecret = field.includes('secret') || field.includes('key');
                  return (
                    <div key={field}>
                      <label className="block text-sm text-gray-300 mb-1">{label} *</label>
                      <div className="relative">
                        <Input
                          type={isSecret && !showSecrets[field] ? 'password' : 'text'}
                          placeholder={`Seu ${label}`}
                          value={credentials[field] || ''}
                          onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
                          className="bg-gray-800 border-gray-700 pr-10"
                        />
                        {isSecret && (
                          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                            onClick={() => setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }))}>
                            {showSecrets[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Modo automatico 24/7 */}
              <Card className={`mt-4 p-3 cursor-pointer transition-all ${autoMode ? 'bg-green-500/20 border-green-500' : 'bg-gray-800/50 border-gray-700'}`}
                onClick={() => setAutoMode(!autoMode)}>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded flex items-center justify-center ${autoMode ? 'bg-green-500' : 'bg-gray-700'}`}>
                    {autoMode && <Check className="w-4 h-4 text-white" />}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Modo Automatico 24/7</p>
                    <p className="text-gray-400 text-xs">O robo publica automaticamente quando novos produtos estiverem disponiveis</p>
                  </div>
                  <Zap className={`w-5 h-5 ml-auto ${autoMode ? 'text-green-400' : 'text-gray-500'}`} />
                </div>
              </Card>

              {/* Seguranca */}
              <Card className="bg-blue-500/10 border-blue-500/30 p-3 mt-3">
                <p className="text-blue-400 text-xs flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Suas credenciais sao armazenadas de forma segura e criptografada. O admin ClickHub monitora todas as conexoes.
                </p>
              </Card>

              {/* Botoes */}
              <div className="flex gap-3 mt-5">
                <Button variant="outline" className="flex-1 border-gray-600" onClick={() => { setShowCredentialsModal(false); setSelectedMarketplace(null); }}>
                  Cancelar
                </Button>
                <Button className="flex-1 bg-orange-500 hover:bg-orange-600" onClick={handleSaveCredentials} disabled={connectingId === selectedMarketplace?.id}>
                  {connectingId === selectedMarketplace?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Conectar e Ativar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Integrations;
